/**
 * Hand-copied mirror of relay/src/services/automationWindDown.ts:33-69 (#300).
 * The relay declares these types inside a service that imports the whole relay
 * runtime, so they cannot cross the build boundary (same situation as
 * src/types/automationStatus.ts). Unlike automation-status, this response has
 * NO schemaVersion — the structural parser below is the only drift guard. Any
 * shape drift must surface as parse-null (rendered as "unknown response"),
 * never as partially-trusted data (#424/#430/#436 malformed-2xx rule).
 */

export type AutomationFamily = 'marty' | 'rita' | 'triggers';
export type AutomationWindDownFamilyStatus = 'drained' | 'residue' | 'unverifiable' | 'failed';

export interface AutomationWindDownFamilyResult {
  family: AutomationFamily;
  status: AutomationWindDownFamilyStatus;
  configured: boolean;
  desiredStateChanged: boolean;
  targetOrderIds: string[];
  remainingOrderIds: string[];
  unclassifiedOrderIds: string[];
  detail: string;
}

export interface OrphanCancelOutcome {
  orderId: string;
  family: AutomationFamily;
  ok: boolean;
  error: string | null;
}

export interface AutomationWindDownResult {
  operationId: string;
  orgId: string;
  actorUserId: string;
  actorRole: 'manager' | 'admin';
  startedAt: number;
  finishedAt: number;
  status: 'complete' | 'partial' | 'failed';
  families: Record<AutomationFamily, AutomationWindDownFamilyResult>;
  orphanCancels: OrphanCancelOutcome[];
}

export const AUTOMATION_FAMILIES: readonly AutomationFamily[] = ['marty', 'rita', 'triggers'];

/**
 * Detail-class snippets, matched with .includes() so relay wording can grow
 * without breaking classification. Sources:
 * - relay/src/kalshi/client.ts:1646 (NoCredentialsError message)
 * - relay/src/services/automationWindDown.ts:405-406 (quiescence timeout)
 */
export const MISSING_CREDENTIALS_SNIPPET = 'No credentials stored';
export const QUIESCENCE_TIMEOUT_SNIPPET = 'did not settle before verification';

const FAMILY_STATUSES = new Set(['drained', 'residue', 'unverifiable', 'failed']);
const OVERALL_STATUSES = new Set(['complete', 'partial', 'failed']);

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isFamilyResult(v: unknown, family: AutomationFamily): v is AutomationWindDownFamilyResult {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  return (
    f.family === family &&
    typeof f.status === 'string' && FAMILY_STATUSES.has(f.status) &&
    typeof f.configured === 'boolean' &&
    typeof f.desiredStateChanged === 'boolean' &&
    isStringArray(f.targetOrderIds) &&
    isStringArray(f.remainingOrderIds) &&
    isStringArray(f.unclassifiedOrderIds) &&
    typeof f.detail === 'string'
  );
}

function isOrphanCancel(v: unknown): v is OrphanCancelOutcome {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.orderId === 'string' &&
    typeof o.family === 'string' && (AUTOMATION_FAMILIES as string[]).includes(o.family) &&
    typeof o.ok === 'boolean' &&
    (o.error === null || typeof o.error === 'string')
  );
}

export function parseAutomationWindDownResult(value: unknown): AutomationWindDownResult | null {
  if (typeof value !== 'object' || value === null) return null;
  const r = value as Record<string, unknown>;
  if (
    typeof r.operationId !== 'string' ||
    typeof r.orgId !== 'string' ||
    typeof r.actorUserId !== 'string' ||
    (r.actorRole !== 'manager' && r.actorRole !== 'admin') ||
    typeof r.startedAt !== 'number' ||
    typeof r.finishedAt !== 'number' ||
    typeof r.status !== 'string' || !OVERALL_STATUSES.has(r.status) ||
    typeof r.families !== 'object' || r.families === null ||
    !Array.isArray(r.orphanCancels) || !r.orphanCancels.every(isOrphanCancel)
  ) {
    return null;
  }
  const families = r.families as Record<string, unknown>;
  for (const family of AUTOMATION_FAMILIES) {
    if (!isFamilyResult(families[family], family)) return null;
  }
  return value as AutomationWindDownResult;
}
